import React, { useContext } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import PageContainer from '../components/PageContainer';
import { OnboardingSteps } from '../components/onboarding/OnboardingSteps';
import { AuthContext } from '../auth';
import { client } from '../api';
import type { OnboardingInfoResponse, OrgMember } from '../api';
import { OnboardingHeroBanner } from '../components/onboarding/OnboardingHeroBanner';
import Onboarding from '../assets/gradientPrescriptionIllustration.svg?react';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
`;

const WelcomeHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const OnboardingPage: React.FC = () => {
  const authContext = useContext(AuthContext);

  const memberQuery = useQuery<{ member: OrgMember }>({
    queryKey: ['orgMember', authContext.user?.email],
    queryFn: () => {
      return client.get('org-member/current-user').then((res) => res.data);
    },
  });

  const onboardingInfoQuery = useQuery<OnboardingInfoResponse>({
    queryKey: ['onboardingInfo'],
    queryFn: () => {
      return client.get('onboarding/info').then((res) => res.data);
    },
  });

  const getWelcomeString = (): string => {
    if (memberQuery.data?.member.name) {
      const splitName = memberQuery.data.member.name.split(' ');
      return `Welcome, ${splitName[0]}!`;
    }
    return 'Welcome!';
  };

  const isAdmin = authContext.user?.is_admin || false;

  if (memberQuery.isLoading || onboardingInfoQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (memberQuery.error || onboardingInfoQuery.error || !onboardingInfoQuery.data) {
    return (
      <PageContainer>
        <PageLoadError />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <WelcomeHeader>
          <Typography styledAs="bodyLargeSpaceGrotesk">{getWelcomeString()}</Typography>
          <Hairline $marginTop="0" $marginBottom="0" />
        </WelcomeHeader>

        <div className="-mb-6 flex items-center justify-between max-md:flex-col max-md:items-start">
          <div className="flex-1">
            <Typography styledAs="h5" renderedAs="h1">
              Tandem will automate prior authorizations, affordability, and pharmacy coordination
            </Typography>
          </div>
          <div className="flex h-[186px] flex-1 flex-col items-center overflow-hidden pt-4 max-sm:hidden">
            <Onboarding className="w-[353px] max-w-full shrink-0" />
          </div>
        </div>

        <OnboardingHeroBanner
          faxVerificationStatus={
            onboardingInfoQuery.data?.practice_statuses.fax_verification_status ?? 'awaiting_account_setup'
          }
          numPrescriptionsBlocked={onboardingInfoQuery.data.num_prescriptions_blocked ?? 0}
          trainingSessionStatus={onboardingInfoQuery.data.practice_statuses.training_session_status}
        />

        <OnboardingSteps isAdmin={isAdmin} practiceOnboardingStatuses={onboardingInfoQuery.data.practice_statuses} />
      </ContentContainer>
    </PageContainer>
  );
};

export default OnboardingPage;
